export const PAAS_COORDS_STALE_AFTER_DAYS = 7;

/** Same as paas.models_ops.MESSAGE_NOT_ENOUGH_RINEX_PREFIX */
export const PAAS_NOT_ENOUGH_RINEX_PREFIX = 'Not enough RINEX data files.';


export const isPaasNotEnoughRinexError = (message: string | undefined | null): boolean => (
	typeof message === 'string' && message.startsWith(PAAS_NOT_ENOUGH_RINEX_PREFIX)
);


export const isPaasCoordsStale = (ageDays: number | null): boolean => (
	ageDays == null || ageDays > PAAS_COORDS_STALE_AFTER_DAYS
);


export type PaasCoordsWarning =
	| { kind: 'none' }
	| { kind: 'stale'; days: number }
	| { kind: 'never' };


export const getPaasCoordsWarning = (
	ages: readonly (number | null)[],
): PaasCoordsWarning => {
	const numericAges = ages.filter((age): age is number => age != null);
	if (numericAges.length === 0) {
		return ages.length === 0 ? { kind: 'none' } : { kind: 'never' };
	}
	const freshestAge = Math.min(...numericAges);
	if (isPaasCoordsStale(freshestAge)) {
		return { kind: 'stale', days: freshestAge };
	}
	return { kind: 'none' };
};


export type PaasTaskProblemView = {
	mountpoint: string;
	has_error: boolean;
	message: string;
	warnings: readonly { filename: string; message: string }[];
};


export const findPaasProblemForMountpoint = (
	problems: readonly PaasTaskProblemView[],
	mountpoint: string,
): PaasTaskProblemView | null => (
	problems.find(problem => problem.mountpoint === mountpoint) ?? null
);


export const formatPaasWarningTooltip = (
	warnings: readonly { filename: string; message: string }[],
): string => (
	warnings.map(warning => `${warning.filename}: ${warning.message}`).join('\n')
);


export const PAAS_SUBTASK_STATUS_DONE = 'done';


export type PaasTaskStationView = {
	mountpoint: string;
	status: string;
	not_enough_rinex: boolean;
};


export type PaasListFilter = 'sentTotal' | 'sentWithMeasurements' | 'solutions' | 'errors';


export const stationMatchesPaasListFilter = (
	station: PaasTaskStationView,
	filter: PaasListFilter,
): boolean => {
	if (filter === 'sentTotal') {
		return true;
	}
	if (filter === 'sentWithMeasurements') {
		return !station.not_enough_rinex;
	}
	if (filter === 'solutions') {
		return station.status === PAAS_SUBTASK_STATUS_DONE;
	}
	return !station.not_enough_rinex && station.status !== PAAS_SUBTASK_STATUS_DONE;
};


export const mountpointsForPaasListFilter = (
	stations: readonly PaasTaskStationView[],
	filter: PaasListFilter,
): ReadonlySet<string> => new Set(
	stations
		.filter(station => stationMatchesPaasListFilter(station, filter))
		.map(station => station.mountpoint),
);


export const countPaasListFilter = (
	stations: readonly PaasTaskStationView[],
	filter: PaasListFilter,
): number => stations.filter(station => stationMatchesPaasListFilter(station, filter)).length;
